<template>
  <!-- Clipping area labels -->
  <div
    ref="wrapper"
    class="slider-wrapper"
    :class="{ 'slider-blacked': blacked }"
  >
    <!-- Clipping area duration label -->
    <div
      ref="durationLabel"
      class="durationLabel"
      :style="{
        left: durationLabel.left,
        'margin-left': durationLabel.marginLeft,
        display: durationLabel.visible ? 'block' : 'none'
      }"
    >
      {{ durationLabel.text }}
    </div>

    <div ref="slider" class="slider">
      <!-- left mark timing -->
      <div
        ref="markInLabel"
        class="markLabel markInLabel"
        :style="{
          left: markInLabel.left,
          'margin-left': markInLabel.marginLeft,
          display: markInLabel.visible == true ? 'block' : 'none'
        }"
      >
        {{ markInLabel.text }}
      </div>
      <!-- right mark timing -->
      <div
        ref="markOutLabel"
        class="markLabel markOutLabel"
        :style="{
          left: markOutLabel.left,
          'margin-left': markOutLabel.marginLeft,
          display: markOutLabel.visible == true ? 'block' : 'none'
        }"
      >
        {{ markOutLabel.text }}
      </div>
    </div>
  </div>
</template>

<script>
import noUISlider from "nouislider";
import {
  getTimeLineWithMs,
  formatDurationWithMs,
  roundToMilliseconds
} from "@/utils/time.js";
import { mapState } from "vuex";
import { mapActions } from "vuex";

const defaultSliderOptions = {
  behaviour: "hover",
  connect: [true, true, true],
  step: 0.001,
  margin: 1
};

// @group Timeline
// Trim timeline slider
export default {
  name: "TimelineRangeSlider",
  props: {
    valueCheckboxRange: {
      type: Boolean,
      default: false
    },
    /** slider start & end values */
    borders: {
      type: Array,
      default: () => [0, 1]
    },
    /** slider paddings from start & end */
    paddings: {
      type: Array,
      default: () => [0, 0]
    },
    /** left handle position */
    sliderLeft: {
      type: Number,
      default: 0
    },
    /** right handle position */
    sliderRight: {
      type: Number,
      default: 1
    },
    /** is slider motion not-linear */
    isSmoothMoving: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    SLIDER_MODE: {
      // Both clipping handles aren't visible
      IN: "in",
      // Only left clipping handle is visible
      LEFT: "left",
      // Only right clipping handle is visible
      RIGHT: "right",
      // Both clipping handles are visible
      BOTH: "both",
      // Invalid handles state
      NONE: "none"
    },
    mathSumRange: 0,
    lockedValues: [],
    /** is slider gragging now */
    dragging: false,
    /** how are handles placed slider */
    sliderMode: {
      /** Handles overflowing indicators
        -1: handle is lefter than left border
         0: handle is in visible borders range
         1: handle is righter than right border
       */
      overflowLeft: 0,
      overflowRight: 0
    },
    /** slider duration label parameters */
    durationLabel: {
      text: "",
      left: 0,
      marginLeft: 30,
      visible: true
    },
    /** left handle time label */
    markInLabel: {
      text: "",
      left: 0,
      marginLeft: 0,
      mode: "right",
      visible: true
    },
    /** right handle time label */
    markOutLabel: {
      text: "",
      left: 0,
      marginLeft: 0,
      mode: "right",
      visible: true
    }
  }),
  computed: {
    ...mapState({
      timezone: state => state.timezone.timezone
    }),
    // @vuese
    // Does user see only not-cropping area
    blacked() {
      return this.cropSliderMode === this.SLIDER_MODE.NONE;
    },
    rangeFixed(){
      return roundToMilliseconds(this.sliderRight - this.sliderLeft);
    },
    // @vuese
    // Return crop borders and view area intersecion mode: <br>
    // <ul>
    //  <li>NONE: user see no borders, external cropping area</li>
    //  <li>LEFT: user see only left border</li>
    //  <li>RIGHT: user see only right border</li>
    //  <li>BOTH: user see both borders</li>
    //  <li>IN: user see no borders, internal cropping area</li>
    // </ul>
    cropSliderMode() {
      if (this.sliderMode.overflowLeft == 0) {
        return this.sliderMode.overflowRight == 0
          ? this.SLIDER_MODE.BOTH
          : this.SLIDER_MODE.LEFT;
      } else if (this.sliderMode.overflowRight == 0) {
        return this.SLIDER_MODE.RIGHT;
      } else {
        return this.sliderMode.overflowRight > 0 &&
          this.sliderMode.overflowLeft < 0
          ? this.SLIDER_MODE.IN
          : this.SLIDER_MODE.NONE;
      }
    },
    // @vuese
    // Slider range object to pass it into slider options
    sliderRange() {
      const [min, max] = this.borders;
      return { min, max };
    }
  },
  watch: {
    valueCheckboxRange() {
      // this.mathSumRange = this.sliderRight - this.sliderLeft;
    },
    // @vuese
    // Update slider borders
    borders() {
      const newOptions = {
        range: this.sliderRange,
        padding: [...this.paddings]
      };

      const shouldReInit = this.updateSliderMode({
        leftBound: this.borders[0],
        rightBound: this.borders[1]
      });

      this.updateSlider(newOptions, shouldReInit);
    },
    // @vuese
    // Update slider paddings
    paddings() {
      if (!this.slider) return;
      this.slider.updateOptions({
        padding: [...this.paddings]
      });
    },
    // @vuese
    // Update slider mode
    sliderLeft(newVal) {
      if (this.sliderMode.overflowLeft) {
        if (this.checkTimeInBorders(newVal)) {
          const reInit = this.updateSliderMode({ leftHandle: newVal });
          this.updateSlider({ range: this.sliderRange }, reInit);
        }
        return;
      }
      const mode = this.cropSliderMode;
      if (mode == this.SLIDER_MODE.BOTH) {
        const unencoded = this.slider.get().map(Number);
        if (unencoded[0] !== newVal) {
          this.slider.set([newVal, unencoded[1]]);
        }
      } else if (mode === this.SLIDER_MODE.LEFT) {
        if (newVal !== Number(this.slider.get())) {
          this.slider.set(newVal);
        }
      }
    },
    // @vuese
    // Update slider mode
    sliderRight(newVal) {
      if (this.sliderMode.overflowRight) {
        if (this.checkTimeInBorders(newVal)) {
          const reInit = this.updateSliderMode({ rightHandle: newVal });
          this.updateSlider({ range: this.sliderRange }, reInit);
        }
        return;
      }
      const mode = this.cropSliderMode;
      if (mode === this.SLIDER_MODE.BOTH) {
        const unencoded = this.slider.get().map(Number);
        if (unencoded[1] !== newVal) {
          this.slider.set([unencoded[0], newVal]);
        }
      } else if (mode === this.SLIDER_MODE.RIGHT) {
        if (newVal !== Number(this.slider.get())) {
          this.slider.set(newVal);
        }
      }
    },
    // @vuese
    // Enable/disable slider animated mode (need for even movement implementation)
    isSmoothMoving() {
      this.slider.updateOptions({
        animate: this.isSmoothMoving
      });
    },
    // @vuese
    // Emiting corresponding event depending on sliders handlers being dragged
    dragging() {
      if (this.dragging) {
        this.$emit("draggingStarted");
      } else {
        this.$emit("draggingStopped");
      }
    },
    // @vuese
    // Emiting corresponding event as well as new Crop Slider Mode if slider mode has been changed
    // @arg newCropSliderMode - new Crop Slider Mode after changing zoom or
    // move to cursor button pressing
    cropSliderMode(newCropSliderMode) {
      this.$emit("cropSliderModeChanged", newCropSliderMode);
    }
  },
  mounted() {
    const options = {
      start: [this.sliderLeft, this.sliderRight],
      ...defaultSliderOptions,
      animate: this.isSmoothMoving,
      range: this.sliderRange,
      padding: [...this.paddings]
    };
    this.initializeSlider(options);

    this.$emit("cropSliderModeChanged", this.cropSliderMode);
  },
  methods: {
    ...mapActions("timeline", [
      "moveSliderLeftBound",
      "moveSliderRightBound",
      "moveSliderLeftBoundFixRange",
      "moveSliderRightBoundFixRange"
    ]),
    // @vuese
    // Handle crop border moving, set dragging 'mutex'
    handleSliderStart(values, handle) {
      this.dragging = handle === 0 ? "left" : "right";
      // User start move crop border
      this.$emit("startChange");
    },
    // @vuese
    // Handle crop border moving end, realease dragging 'mutex'
    handleSliderEnd(args) {
      if (this.SLIDER_MODE.BOTH) {
        if (this.dragging == "left") {
          // User moved slider to left
          this.$emit("sliderMarkInMove", roundToMilliseconds(args[0]));
        } else if (this.dragging == "right") {
          // User moved slider to right
          this.$emit("sliderMarkOutMove", roundToMilliseconds(args[1]));
        }
      } else if (this.SLIDER_MODE.LEFT) {
        // User moved slider to left
        this.$emit("sliderMarkInMove", roundToMilliseconds(args[0]));
      } else if (this.SLIDER_MODE.RIGHT) {
        // User moved slider to right
        this.$emit("sliderMarkInMove", roundToMilliseconds(args[1]));
      }
      this.dragging = false;
      // User finished crop border moving
      this.$emit("endChange");
    },
    // @vuese
    // Move handlers dependency to visible area
    sliderUpdateHandler(values, handle, unencoded, tap, positions) {
      const mode = this.cropSliderMode;
      let u0 = unencoded[0];
      let u1 = unencoded[1];
      let p0 = positions[0];
      let p1 = positions[1];

      if (mode === this.SLIDER_MODE.LEFT) {
        u1 = this.sliderRight;
        p1 = 100;
      } else if (mode === this.SLIDER_MODE.RIGHT) {
        u1 = unencoded[0];
        u0 = this.sliderLeft;
        p1 = positions[0];
        p0 = 0;
      }
      /** calc interval duration */
      const text = formatDurationWithMs(u1 - u0);
      const marginLeft = -this.$refs.durationLabel.offsetWidth / 2 + "px";
      const left = p0 + (p1 - p0) / 2 + "%";
      this.durationLabel = Object.assign({}, this.durationLabel, {
        marginLeft,
        left,
        text
      });

      this.updateMarkLabels(u0, u1, p0, p1);
    },
    // @vuese
    // Update mark labels position
    updateMarkLabels(leftTime, rightTime, markInLeft, markOutLeft) {
      const mode = this.cropSliderMode;
      /** calc left slider label */
      if (mode === this.SLIDER_MODE.LEFT || mode === this.SLIDER_MODE.BOTH) {
        this.updateLeftMarkLabel(leftTime, markInLeft);
      }
      /** calc right slider label */
      if (mode === this.SLIDER_MODE.RIGHT || mode === this.SLIDER_MODE.BOTH) {
        this.updateRightMarkLabel(rightTime, markOutLeft);
      }
    },
    // @vuese
    // Update left mark label position & text
    updateLeftMarkLabel(leftTime, markInLeft) {
      /** calc left slider label */
      const markInText = getTimeLineWithMs(leftTime, this.timezone);
      const markInParentRect = this.$refs.slider.getBoundingClientRect();
      const markInRect = this.$refs.markInLabel.getBoundingClientRect();

      let markInLeftPos = markInRect.left;
      if (this.markInLabel.mode == "right") {
        markInLeftPos -= markInRect.width;
      }

      let markInMarginLeft = 0;
      let markInMode = "right";

      if (markInLeftPos > markInParentRect.left) {
        markInMarginLeft = Math.ceil(-(markInRect.width + 10));
        markInMode = "left";
      }

      this.markInLabel = Object.assign({}, this.markInLabel, {
        marginLeft: markInMarginLeft + "px",
        mode: markInMode,
        left: markInLeft + "%",
        text: markInText,
        visible: true
      });
    },
    // @vuese
    // Update right mark label position & text
    updateRightMarkLabel(rightTime, markOutLeft) {
      const markOutText = getTimeLineWithMs(rightTime, this.timezone);
      const markOutParentRect = this.$refs.slider.getBoundingClientRect();
      const markOutRect = this.$refs.markOutLabel.getBoundingClientRect();

      let markOutRightPos = markOutRect.right;
      if (this.markOutLabel.mode == "left") {
        markOutRightPos += markOutRect.width;
      }

      let markOutMarginLeft = Math.ceil(-markOutRect.width);
      let markOutMode = "left";

      if (markOutRightPos < markOutParentRect.right) {
        markOutMarginLeft = 10;
        markOutMode = "right";
      }

      this.markOutLabel = Object.assign({}, this.markOutLabel, {
        marginLeft: markOutMarginLeft + "px",
        mode: markOutMode,
        left: markOutLeft + "%",
        text: markOutText,
        visible: true
      });
    },
    // @vuese
    // Change slider borders
    sliderChangeHandler(values, handle, unencoded) {
      const mode = this.cropSliderMode;
      let left = unencoded[0];
      let right = unencoded[1];

      if (mode === this.SLIDER_MODE.LEFT) {
        right = this.sliderRight;
      } else if (mode === this.SLIDER_MODE.RIGHT) {
        left = this.sliderLeft;
        right = unencoded[0];
      }
      left = roundToMilliseconds(left);
      right = roundToMilliseconds(right);

      if (left !== this.sliderLeft || right !== this.sliderRight) {
        this.$emit("rangeChanged", [left, right]);
      }
    },
    // @vuese
    // Create slider
    initializeSlider(options) {
      if (this.slider) {
        this.slider.destroy();
      }
      this.slider = noUISlider.create(this.$refs.slider, options);
      this.slider.on("update", this.sliderUpdateHandler);
      this.slider.on("change", this.sliderChangeHandler);
      this.slider.on("start", this.handleSliderStart);
      this.slider.on("end", this.handleSliderEnd);
      this.slider.on("slide", (values, handle) => {
        if (this.valueCheckboxRange == true) {
          const leftValue = roundToMilliseconds(parseFloat(values[0]));
          const rightValue = roundToMilliseconds(parseFloat(values[1]));
          if (handle == 0) {
            this.slider.setHandle(
              1,
              leftValue + this.rangeFixed,
              true,
              true
            );
          } else {
            this.slider.setHandle(
              0,
              rightValue - this.rangeFixed,
              true,
              true
            );

          }
        }
        this.$emit("changing", roundToMilliseconds(parseFloat(values[handle])));
      });
    },
    // @vuese
    // Update slider options
    updateSlider(options, reinitialize = true) {
      const mode = this.cropSliderMode;

      if (!reinitialize) {
        if (this.slider) {
          this.slider.updateOptions(options);
        }
        return;
      }
      let usedSliderOptions = defaultSliderOptions;

      if (this.slider) {
        usedSliderOptions = this.slider.options;
        this.slider.destroy();
        this.slider = null;
      }
      this.durationLabel.visible = mode !== this.SLIDER_MODE.NONE;
      /** timing lable visibility */
      this.markInLabel.visible =
        mode == this.SLIDER_MODE.LEFT || mode == this.SLIDER_MODE.BOTH;
      this.markOutLabel.visible =
        mode == this.SLIDER_MODE.RIGHT || mode == this.SLIDER_MODE.BOTH;
      if (mode === this.SLIDER_MODE.IN || mode === this.SLIDER_MODE.NONE) {
        return;
      }

      console.log(`reinit ${mode}`);
      const newOptions = {
        ...usedSliderOptions,
        ...options,
        start:
          mode === this.SLIDER_MODE.BOTH
            ? [this.sliderLeft, this.sliderRight]
            : mode === this.SLIDER_MODE.LEFT
            ? this.sliderLeft
            : this.sliderRight,
        connect:
          mode === this.SLIDER_MODE.BOTH
            ? [true, true, true]
            : mode === this.SLIDER_MODE.LEFT
            ? [true, false]
            : [false, true]
      };

      this.slider = noUISlider.create(this.$refs.slider, newOptions);
      /* not sure about this: append labels */
      this.$refs.slider.appendChild(this.$refs.markInLabel);
      this.$refs.slider.appendChild(this.$refs.markOutLabel);
      /** not sure about this: reinit events */
      this.slider.on("update", this.sliderUpdateHandler);
      this.slider.on("change", this.sliderChangeHandler);
      this.slider.on("start", this.handleSliderStart);
      this.slider.on("end", this.handleSliderEnd);
      this.slider.on("slide", (values, handle) => {
        this.$emit("changing", values[handle]);
      });
      if (mode === this.SLIDER_MODE.RIGHT) {
        // right-handle
        const handle = this.$refs.slider.querySelector(".noUi-handle");
        handle.classList.add("right-handle");
      }
      window.slider = this.slider;
    },
    updateSliderMode({
      leftHandle = this.sliderLeft,
      rightHandle = this.sliderRight,
      leftBound = this.borders[0],
      rightBound = this.borders[1]
    }) {
      const newSliderMode = { ...this.sliderMode };

      if (leftHandle < leftBound) {
        if (rightHandle < leftBound) {
          // (LH RH) |LB RB|
          newSliderMode.overflowLeft = -1;
          newSliderMode.overflowRight = -1;
        } else if (rightHandle > rightBound) {
          // (LH |LB RB| RH)
          newSliderMode.overflowLeft = -1;
          newSliderMode.overflowRight = 1;
        } else {
          // (LH |LB RH) RB|
          newSliderMode.overflowLeft = -1;
          newSliderMode.overflowRight = 0;
        }
      } else {
        if (leftHandle > rightBound) {
          // |LB RB| (LH RH)
          newSliderMode.overflowLeft = 1;
          newSliderMode.overflowRight = 1;
        } else if (rightHandle > rightBound) {
          // |LB (LH RB| RH)
          newSliderMode.overflowLeft = 0;
          newSliderMode.overflowRight = 1;
        } else {
          // |LB (LH RH) RB|
          newSliderMode.overflowLeft = 0;
          newSliderMode.overflowRight = 0;
        }
      }
      const modified =
        JSON.stringify(this.sliderMode) !== JSON.stringify(newSliderMode);
      this.sliderMode = Object.assign({}, this.sliderMode, newSliderMode);
      return modified;
    },
    checkTimeInBorders(time) {
      return time > this.borders[0] && time < this.borders[1];
    }
  }
};
</script>

<style lang="scss">
.slider-wrapper,
.slider {
  height: 100%;
}

.noUi-base {
  position: relative;
  max-height: 120px;
}
.noUi-origin {
  height: 100%;
  position: absolute;
  top: 0;
  z-index: var(--timeline-left-handle-z) !important;

  &:last-child {
    z-index: var(--timeline-right-handle-z) !important;
  }
}
.slider.noUi-target {
  height: 100%;
  z-index: var(--timeline-slider-z);
  background: transparent;
  border-radius: unset;
  border: unset;
  box-shadow: unset;
}

.slider-blacked .slider {
  background: rgba(0, 0, 0, 0.5) !important;
  z-index: var(--timeline-faded-z);
  position: relative;
}

.slider.noUi-horizontal .noUi-handle {
  height: 100%;
  top: 0;
  width: 10px;
  border-radius: 0;
  border: none;
  cursor: ew-resize;
  box-shadow: unset;
}

html:not([dir="rtl"]) .slider.noUi-horizontal .noUi-handle {
  right: 0;
  background: #e6a23c;
  left: auto;
}

.right-handle {
  background: #f56c6c !important;
  right: -10px !important;
  left: auto !important;
}

html:not([dir="rtl"]) .slider.noUi-horizontal .noUi-handle-upper {
  background: #f56c6c;
  right: -10px;
  left: auto;
}

.slider.noUi-horizontal .noUi-origin {
  height: 100%;
}

.slider.noUi-horizontal .noUi-handle:before,
.slider.noUi-horizontal .noUi-handle:after {
  content: "";
  display: block;
  position: absolute;
  height: 14px;
  width: 1px;
  background: #222222;
  left: 3px;
  top: 50%;
  margin-top: -7px;
}

.slider.noUi-horizontal .noUi-handle:after {
  left: 6px;
}

.noUi-handle:focus {
  outline: unset;
}

.slider .noUi-connects {
  border-radius: unset;
}

.slider .noUi-connect {
  background: rgba(0, 0, 0, 0.7);
}

.slider .noUi-connects .noUi-connect:nth-child(2) {
  display: flex;
  background: none;
}

.durationLabel {
  position: absolute;
  left: 0;
  z-index: var(--timeline-label-z);
  padding: 4px;

  /** vertical center alignment */
  top: 50%;
  transform: translateY(-50%);

  background-color: #272727;
  color: #ffffff;
}

.markLabel {
  position: absolute;
  //bottom: 45px; //8px;
  left: 0;
  z-index: var(--timeline-duration-z);
  padding: 5px;
  color: #ffffff;
}
.markInLabel {
  background-color: #fff395 !important;
  color: #000000 !important;
  bottom: 15px;
}
.markOutLabel {
  background-color: #ff0f88 !important;
  top: 15px;
}

.timeline-button,
.timeline-button:active,
.timeline-button:hover {
  border: none;
  outline: none;
  position: absolute;
  top: 50%;
  margin-top: -20px;
  z-index: var(--timeline-button-z);
  height: 40px;
  width: 24px;
  background-color: #272727;
  color: #ffffff;
  cursor: pointer;
}

.timeline-button:hover {
  background-color: #393939;
}

.timeline-button-left {
  left: 0;
}

@mixin extend-timeline-button-decoration {
  content: "";
  position: absolute;
  display: block;
  width: 0;
  height: 0;
  z-index: var(--timeline-button-z);
  border: 10px solid transparent;
  border-left: 0;
  bottom: -10px;
}

//noinspection CssReplaceWithShorthandSafely
.timeline-button-left:before {
  @include extend-timeline-button-decoration;
  border-top: 0;
  border-right-color: #1b1b1b;
  left: 0;
  display: none;
}

.timeline-button-right {
  right: 0;
}

.timeline-button-right:before {
  @include extend-timeline-button-decoration;
  border-bottom: 0;
  border-top-color: #1b1b1b;
  right: 0;
  display: none;
}
</style>
